/**
 * useListSort.ts — the column sort behind an Admin list's clickable headers.
 *
 * Every Admin list table looks like the original Users list (the shared
 * components/ui/DataTable): a sortable column's title is clickable, shows a
 * ChevronsUpDown until it is the sort column and then ChevronUp / ChevronDown.
 * Clicking the sort column reverses it; clicking another column sorts by that
 * one, ascending.
 *
 * The page filters, this sorts, useListPagination slices:
 *
 *   const sorting = useListSort(filtered, { initialKey: 'name', compare: { name: byName, code: byCode } });
 *   const pagination = useListPagination(sorting.sorted, { resetOn: [...] });
 *   <AdminListTable columns={COLUMNS} sort={sorting.sort} onSortChange={sorting.onSortChange}>
 *
 * `initialKey` is the column the page's order already follows (it shows as
 * the sort column from the start). Pass null when the page's order is not a
 * column's (an API or saved order): the rows stay in the order given until
 * someone clicks a title.
 *
 * `compare` holds an ascending comparator per sortable column key; descending
 * reverses it. The sort is stable, so rows that tie keep the page's order.
 * A comparator built on page state (lookups, counts) re-sorts whenever it
 * changes; a module-level or memoised one sorts only when the rows or the
 * sort change.
 */
import { useCallback, useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

/** The sort column and its direction, as AdminListTable's `sort` prop takes it. */
export interface ListSort {
  key: string;
  dir: SortDir;
}

export type ListSortCompare<T> = Record<string, (a: T, b: T) => number>;

export interface UseListSortOptions<T> {
  /** The column the list opens sorted by; null (the default) keeps the given order. */
  initialKey?: string | null;
  initialDir?: SortDir;
  /** Ascending comparator per sortable column key. */
  compare: ListSortCompare<T>;
}

export interface ListSortState<T> {
  /** The rows in the current sort (the given order while nothing is sorted). */
  sorted: T[];
  /** The sort column, or undefined while the list keeps its given order. */
  sort: ListSort | undefined;
  /** A header click: reverses the sort column, or sorts by another one ascending. */
  onSortChange: (key: string) => void;
  /** Sets the sort outright (undefined goes back to the given order). */
  setSort: (sort: ListSort | undefined) => void;
}

/** Text comparison for a column: case-insensitive, numbers in text read as numbers ("Item 2" before "Item 10"). */
export function compareText(a: string | null | undefined, b: string | null | undefined): number {
  return (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base', numeric: true });
}

/** Number comparison for a column; blanks sort as 0. */
export function compareNumber(a: number | string | null | undefined, b: number | string | null | undefined): number {
  return (Number(a) || 0) - (Number(b) || 0);
}

export function useListSort<T>(
  items: T[],
  { initialKey = null, initialDir = 'asc', compare }: UseListSortOptions<T>,
): ListSortState<T> {
  const [sort, setSortState] = useState<ListSort | undefined>(
    initialKey == null ? undefined : { key: initialKey, dir: initialDir },
  );

  const onSortChange = useCallback((key: string) => {
    setSortState((prev) =>
      prev && prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  }, []);

  const setSort = useCallback((next: ListSort | undefined) => setSortState(next), []);

  const comparator = sort ? compare[sort.key] : undefined;
  const dir = sort?.dir;

  const sorted = useMemo(() => {
    if (!comparator) return items;
    const sign = dir === 'desc' ? -1 : 1;
    return [...items].sort((a, b) => sign * comparator(a, b));
  }, [items, comparator, dir]);

  return { sorted, sort, onSortChange, setSort };
}
